// Boids-style flocking. Each ally is pulled toward the cursor (primary
// attractor), nudged toward neighbor average position (cohesion), away
// from too-close neighbors (separation), and toward neighbor average
// velocity (alignment). All three weights are heritable (taken from the
// resolved StatSheet) and per-ball.

import type { WorldState } from '../world-state';
import { C } from '../components';
import type { Position, Velocity, Stats, FactionComp } from '../components';

const NEIGHBOR_RADIUS = 70;
const SEPARATION_RADIUS = 24;
const CURSOR_PULL = 220; // base accel toward cursor (units/sec)
const MAX_SPEED_HARD_CAP = 220;

export function flockingSystem(ws: WorldState, dt: number): void {
  const { world } = ws;
  const positions = world.store<Position>(C.position);
  const velocities = world.store<Velocity>(C.velocity);
  const stats = world.store<Stats>(C.stats);
  const factions = world.store<FactionComp>(C.faction);

  // Rebuild ally hash for neighborhood queries.
  ws.allyHash.clear();
  positions.forEach((pos, id) => {
    const f = factions.get(id);
    if (f?.side === 'ally') ws.allyHash.insert(id, pos.x, pos.y);
  });

  positions.forEach((pos, id) => {
    const f = factions.get(id);
    if (f?.side !== 'ally') return;
    const vel = velocities.get(id);
    const s = stats.get(id);
    if (!vel || !s) return;

    const sheet = s.sheet;

    // Cursor attractor.
    const dxC = ws.cursor.x - pos.x;
    const dyC = ws.cursor.y - pos.y;
    const distC = Math.hypot(dxC, dyC) || 1;
    let ax = (dxC / distC) * CURSOR_PULL;
    let ay = (dyC / distC) * CURSOR_PULL;

    // Neighborhood (cohesion + alignment + separation).
    let cohX = 0,
      cohY = 0,
      aliX = 0,
      aliY = 0,
      sepX = 0,
      sepY = 0;
    let nCount = 0;
    ws.allyHash.forEachInRadius(pos.x, pos.y, NEIGHBOR_RADIUS, (nid) => {
      if (nid === id) return;
      const np = positions.get(nid);
      const nv = velocities.get(nid);
      if (!np || !nv) return;
      const dx = np.x - pos.x;
      const dy = np.y - pos.y;
      const d = Math.hypot(dx, dy);
      if (d > NEIGHBOR_RADIUS) return;
      nCount++;
      cohX += np.x;
      cohY += np.y;
      aliX += nv.vx;
      aliY += nv.vy;
      if (d < SEPARATION_RADIUS && d > 0.001) {
        // Push away, stronger when closer.
        const push = (SEPARATION_RADIUS - d) / SEPARATION_RADIUS;
        sepX -= (dx / d) * push;
        sepY -= (dy / d) * push;
      }
    });

    if (nCount > 0) {
      const invN = 1 / nCount;
      const cohCenterX = cohX * invN;
      const cohCenterY = cohY * invN;
      const cohDx = cohCenterX - pos.x;
      const cohDy = cohCenterY - pos.y;
      const cohMag = Math.hypot(cohDx, cohDy) || 1;
      ax += (cohDx / cohMag) * 40 * sheet.cohesion;
      ay += (cohDy / cohMag) * 40 * sheet.cohesion;
      const aliMag = Math.hypot(aliX * invN, aliY * invN) || 1;
      ax += ((aliX * invN) / aliMag) * 25 * sheet.alignment;
      ay += ((aliY * invN) / aliMag) * 25 * sheet.alignment;
    }
    ax += sepX * 220 * sheet.separation;
    ay += sepY * 220 * sheet.separation;

    // Heavier balls accelerate more slowly (mass as inertia divisor).
    const massDiv = Math.max(0.1, sheet.mass);
    ax /= massDiv;
    ay /= massDiv;

    // Integrate velocity. Light drag.
    let nvx = vel.vx + ax * dt;
    let nvy = vel.vy + ay * dt;
    nvx *= 1 - Math.min(1, 0.9 * dt);
    nvy *= 1 - Math.min(1, 0.9 * dt);

    // Clamp to per-ball max (speed trait drives this).
    const maxSpeed = Math.min(MAX_SPEED_HARD_CAP, sheet.speed);
    const sp = Math.hypot(nvx, nvy);
    if (sp > maxSpeed) {
      nvx = (nvx / sp) * maxSpeed;
      nvy = (nvy / sp) * maxSpeed;
    }
    vel.vx = nvx;
    vel.vy = nvy;

    // Integrate position.
    pos.x += nvx * dt;
    pos.y += nvy * dt;

    // Soft world bounds.
    const b = ws.bounds;
    if (pos.x < b.minX) {
      pos.x = b.minX;
      vel.vx = Math.max(0, vel.vx);
    }
    if (pos.x > b.maxX) {
      pos.x = b.maxX;
      vel.vx = Math.min(0, vel.vx);
    }
    if (pos.y < b.minY) {
      pos.y = b.minY;
      vel.vy = Math.max(0, vel.vy);
    }
    if (pos.y > b.maxY) {
      pos.y = b.maxY;
      vel.vy = Math.min(0, vel.vy);
    }
  });
}
